import appStore from "@/store/modules/appStore";
import errorLoggerService from "@/app/errors/errorLoggerService";

export * from "@/components/composables/reactivityTypes";

// Overload 1: Accepts a function that returns a promise
export async function wrapLoading<T>(
  promise: Promise<T> | (() => Promise<T>)
): Promise<T> {
  try {
    appStore.setLoading(true);
    return await (typeof promise === "function" ? promise() : promise);
  } finally {
    appStore.setLoading(false);
  }
}

// https://stackoverflow.com/questions/62325850/rewrite-error-catching-higher-order-function-to-catch-async-errors

/**
 * Adapts the given function to display a loading dialog while preserving arguments and return type.
 */
export function withLoading<T extends any[], U>(
  fn: (...args: T) => PromiseLike<U>
): (...args: T) => Promise<U> {
  return async (...args) => {
    try {
      appStore.setLoading(true);
      return await fn(...args);
    } finally {
      appStore.setLoading(false);
    }
  };
}

/**
 * Errors thrown by the API within async watcher functions do not propagate throughout the app correctly
 * This function wraps the behaviour, but should not be replacement unless required.
 *
 * https://github.com/vuejs/vue/issues/10009
 *
 * Should be fixed by version 2.6.13 but errors still not showing
 */
export function wrapPromiseWithErrorHandling<T extends any[], U>(
  fn: (...args: T) => PromiseLike<U>
): (...args: T) => Promise<U> {
  return async (...args) => {
    try {
      return await fn(...args);
    } catch (error: any) {
      errorLoggerService.logError("Error from promise wrapper", error);
      appStore.showError(error);
      throw error;
    }
  };
}
